/**
 * Timezone-safe date parsing for backend date values.
 *
 * The backend stores calendar dates as date-only strings ("2026-06-08" or
 * "2026-06-08T00:00:00.000Z"). `new Date("2026-06-08")` parses that as UTC
 * midnight, so any local-time formatting (e.g. toLocaleDateString) in a
 * timezone behind UTC renders the PREVIOUS day. These helpers parse the
 * calendar date into local time so the date the user entered is the date
 * that displays and exports.
 */

const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/** Parse a backend date value as a LOCAL calendar date (no UTC shift). */
export function parseLocalDate(value: string | Date | null | undefined): Date {
  if (value == null || value === "") return new Date(NaN);
  if (value instanceof Date) return value;
  const match = String(value).match(ISO_DATE_PREFIX);
  if (match) {
    const [, year, month, day] = match;
    return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  }
  return new Date(value);
}

/** Format a backend date value for display, timezone-safe. */
export function formatLocalDate(
  value: string | Date | null | undefined,
  fallback = "N/A"
): string {
  const d = parseLocalDate(value);
  return isNaN(d.getTime()) ? fallback : d.toLocaleDateString();
}
